import { useCallback } from 'react';

import type { RecordSourceSelectorProxy } from 'relay-runtime';

/**
 * Pure utility functions for board ordering and filtering logic
 * These functions handle the business logic of reordering favorite boards
 * and filtering boards based on user preferences.
 */

// Types for the board ordering utilities
export type BoardEdge = {
	node?: {
		id?: string | null;
		boardId?: number | string | null;
		name?: string | null;
		favouriteValue?: {
			isFavourite?: boolean | null;
		} | null;
		defaultNavigationItem?: {
			url?: string | null;
		} | null;
	} | null;
} | null;

export type SidebarOrderItem = {
	menuId: string;
	visible: boolean;
};

export type BoardData = {
	id: string;
	name: string;
	favourite: boolean;
};

/**
 * Transforms board edges into a simplified board data structure
 * Pure function - easily testable
 */
export function transformBoardEdgesToData(boardEdges: readonly BoardEdge[]): BoardData[] {
	return boardEdges
		.map((edge) => {
			if (!edge?.node?.boardId) {
				return null;
			}

			return {
				id: String(edge.node.boardId),
				name: edge.node.name || '',
				favourite: edge.node.favouriteValue?.isFavourite || false,
			};
		})
		.filter((board): board is BoardData => board !== null);
}

/**
 * Gets the total count of favorite boards
 * Pure function - utility for UI logic
 */
export function getFavoriteBoardCount(boardEdges: readonly BoardEdge[]): number {
	return boardEdges.filter((edge) => edge?.node?.favouriteValue?.isFavourite === true).length;
}

/**
 * Type-safe wrapper for GraphQL board edges
 * Converts GraphQL types to our BoardEdge type safely
 */
export function convertGraphQLBoardEdges(graphQLEdges: readonly unknown[]): readonly BoardEdge[] {
	// This function ensures type safety without assertions
	// The unknown type is constrained to this single conversion point
	return graphQLEdges.filter((edge): edge is BoardEdge => {
		return edge != null && typeof edge === 'object';
	});
}

/**
 * Generic version that works with GraphQL edge types
 * Applies custom ordering while preserving original edge types
 */
export function getOrderedFavoriteBoardsWithOriginalTypes<
	T extends {
		node?: {
			boardId?: number | string | null;
			favouriteValue?: { isFavourite?: boolean | null } | null;
			[key: string]: unknown; // Allow additional properties from GraphQL
		} | null;
	},
>(boardEdges: readonly (T | null | undefined)[], sidebarOrder: SidebarOrderItem[]): T[] {
	// Filter out null/undefined edges and ensure we have valid edges
	const validEdges = boardEdges.filter((edge): edge is T => edge != null);

	// Filter to only favorite boards
	const favoriteBoards = validEdges.filter(
		(edge) => edge?.node?.favouriteValue?.isFavourite === true,
	);

	if (sidebarOrder.length === 0) {
		// No custom order preferences, return favorites in original order
		return favoriteBoards;
	}

	// Create a map for quick board lookup
	const boardMap = new Map<string, T>();
	favoriteBoards.forEach((boardEdge) => {
		if (boardEdge?.node?.boardId) {
			boardMap.set(boardEdge.node.boardId.toString(), boardEdge);
		}
	});

	// Track which boards we've already included in the ordered list
	const usedBoardIds = new Set<string>();

	// First, get boards in the order specified by sidebar preferences
	const orderedFavoriteBoards = sidebarOrder
		.map((sidebarItem) => {
			if (!sidebarItem.menuId || !sidebarItem.visible) {
				return null;
			}

			const boardEdge = boardMap.get(sidebarItem.menuId);

			// Only include if it's actually a favorite board
			if (boardEdge?.node?.favouriteValue?.isFavourite) {
				usedBoardIds.add(sidebarItem.menuId);
				return boardEdge;
			}

			return null;
		})
		.filter((boardEdge): boardEdge is T => boardEdge !== null);

	// Then add any favorite boards not in the sidebar order (newly favorited boards)
	const remainingFavoriteBoards = favoriteBoards.filter((boardEdge) => {
		if (!boardEdge?.node?.boardId) {
			return false;
		}
		return !usedBoardIds.has(boardEdge.node.boardId.toString());
	});

	return [...orderedFavoriteBoards, ...remainingFavoriteBoards];
}

/**
 * Evaluates if there are any favorite boards available
 */
export function hasFavoriteBoards(boardEdges: readonly BoardEdge[]): boolean {
	return getFavoriteBoardCount(boardEdges) > 0;
}

/**
 * Evaluates if there are more favorite boards than can be displayed
 * This is used to determine if pagination or "view more" is needed
 */
export function hasMoreFavoriteBoards(
	boardEdges: readonly BoardEdge[],
	maxDisplayCount: number,
): boolean {
	const favoriteCount = getFavoriteBoardCount(boardEdges);
	return favoriteCount > maxDisplayCount;
}

/**
 * Creates a store reader function for getting current boards and sidebar order from Relay store
 * This function is designed to be passed to the platform drag-and-drop components
 *
 * NOTE: This function duplicates some transformation logic from transformBoardEdgesToData,
 * but working directly with Relay store records requires different type handling.
 * If this grows more complex, consider extracting common patterns.
 */
export function createBoardsStoreReader(entityAri: string, ownerAri: string, projectKey: string) {
	return (store: RecordSourceSelectorProxy) => {
		// Read current sidebar order from navigation customisation
		const currentOrder = store.getRoot().getLinkedRecord('settings_navigationCustomisation', {
			entityAri,
			ownerAri,
		});
		const sidebar = currentOrder?.getLinkedRecord('sidebar');
		const nodes = sidebar?.getLinkedRecords('nodes');

		const currentSidebarOrder = (nodes || [])
			.map((node) => {
				if (!node) return null;
				const menuId = node.getValue('menuId');
				const visible = node.getValue('visible');
				return menuId && typeof visible === 'boolean' ? { menuId: String(menuId), visible } : null;
			})
			.filter((item): item is NonNullable<typeof item> => item !== null);

		// Read current boards from project data
		// This duplicates logic from transformBoardEdgesToData but works directly with store records
		const projectRecord = store.getRoot().getLinkedRecord('jiraProjectByKey', { projectKey });
		const boardsConnection = projectRecord?.getLinkedRecord('boards', {
			first: 50, // Use same default as in the component
		});
		const boardEdges = boardsConnection?.getLinkedRecords('edges') || [];

		const currentItems = boardEdges
			.map((edge) => {
				if (!edge) return null;
				const node = edge.getLinkedRecord('node');
				if (!node) return null;

				const boardId = node.getValue('boardId');
				const name = node.getValue('name');
				const favouriteValue = node.getLinkedRecord('favouriteValue');
				const isFavourite = favouriteValue?.getValue('isFavourite');

				if (!boardId || !name) return null;

				return {
					id: String(boardId),
					name: String(name),
					favourite: Boolean(isFavourite),
				};
			})
			.filter((board): board is NonNullable<typeof board> => board !== null);

		return {
			currentItems,
			currentSidebarOrder,
		};
	};
}

/**
 * Creates a simple store reader function for drag-and-drop operations
 * This returns a function that can read current data from the Relay store
 */
export function useBoardsStoreReader(
	entityAri: string,
	ownerAri: string,
	projectKey: string,
	currentBoards: Array<{ favourite?: boolean; id: string; name: string }>,
	currentSidebarOrder: Array<{ menuId: string; visible: boolean }>,
) {
	return useCallback(() => {
		// First try to get fresh data from the Relay store
		try {
			// We need access to the Relay environment store here
			// For now, fall back to current props until we can implement proper store access

			// Return current props for now, but this should be replaced with actual store reading
			return {
				currentItems: currentBoards,
				currentSidebarOrder,
			};
		} catch (error) {
			// Failed to read from Relay store, using current props
			return {
				currentItems: currentBoards,
				currentSidebarOrder,
			};
		}
	}, [currentBoards, currentSidebarOrder]);
}
